import { InjectionToken } from '@angular/core';
import {
    CalibrationModel,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    NotificationIdentifierModel,
    NotificationModel,
} from '@evorait/pace-core';
import { ParticipantCopyId, ParticipantParticulars } from '@evorait/pace-forms';
import { Observable } from 'rxjs';

/**
 * Public contract of the confirmation data service: loading, saving, and submitting a
 * technician's confirmation, plus the handful of related notification-status signals the
 * confirmation screens depend on.
 *
 * Implemented by both the legacy `ConfirmationService` (concurrency-store-backed) and the
 * NgRx-based `ConfirmationNgrxService`; consumers depend on this interface via
 * {@link ConfirmationDataInstanceToken} rather than on a concrete class.
 *
 * This interface — and its NgRx implementation and store slice — live in `apps/pace` rather
 * than `pace-core`: the submit pipeline's collaborators (`OdataConfirmationService`,
 * `MileageService`, `PCHAlertService`, `LocalStorageSalesQuoteService`) are app-local, and
 * `saveAttendee`/`removeAttendee`/`getParticipantsFromAttendees` use `pace-forms` types —
 * `pace-core` must not depend on either.
 */
export interface ConfirmationDataInterface {
    /** Emits whenever the currently open confirmation was found to be already completed (e.g. from another device). */
    readonly confirmationCompletedPreviously$: Observable<void>;

    /** Load the confirmation for `id`/`type`, choosing the locally stored copy or a freshly constructed one. */
    getConfirmation(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): Observable<ConfirmationModel>;

    /** Persist the confirmation locally (concurrency store / IndexedDB), stamping `timestamp` as the save time. */
    saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel>;

    /** Submit the confirmation to the backend and run the full post-submit cleanup pipeline. */
    submitConfirmation(confirmation: ConfirmationModel): Observable<void>;

    /** Reconcile a confirmation that was completed on another device: save its CRM status and drop the local copy. */
    confirmationCompletedPreviouslyCleanup(confirmation: ConfirmationModel): Observable<boolean>;

    /** Manually signal that the current confirmation was already completed elsewhere. */
    triggerConfirmationCompletedPreviously(): void;

    /** Defer signing: move the notification's status forward so the technician can sign it later. */
    signLater(notification: NotificationModel): void;

    /** One-shot OData lookup of a measurement tool's calibration date. */
    getCalibrationDate(measurementToolId: string): Observable<CalibrationModel>;

    /** PACE US only. */
    getParticipantsFromAttendees(): Observable<ParticipantParticulars[]>;

    /** PACE US only. */
    saveAttendee(): Observable<void>;

    /** PACE US only. */
    removeAttendee(): Observable<ParticipantCopyId>;
}

/**
 * DI token for {@link ConfirmationDataInterface}. Lets the app choose which implementation to
 * provide (legacy `ConfirmationService` or the NgRx-based `ConfirmationNgrxService`) without
 * consumers depending on a concrete class.
 */
export const ConfirmationDataInstanceToken = new InjectionToken<ConfirmationDataInterface>('ConfirmationDataInterface');
